import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

// Helpers
const helper = name => {
	switch (name) {
		case 'youTubeEmbedOptions':
			return (
				<div>
					<p>The following variables are replaced in the string:</p>
					<ul>
						<li><strong>$width</strong> &ndash; Width from the youtubeVideoWidth option</li>
						<li><strong>$height</strong> &ndash; Height from the youtubeVideoHeight option</li>
						<li><strong>$id</strong> &ndash; ID of YouTube video</li>
					</ul>
				</div>);
		case 'postLinkFormatOptions':
			return (
				<div>
					<p>The following variables are replaced in the URL:</p>
					<ul>
						<li><strong>$id</strong> &ndash; ID of the post</li>
						<li><strong>$slug</strong> &ndash; The SEO text added to public links to the post</li>
					</ul>
					<pre className="language-javascript"><code className="language-javascript">{"{ postLinkFormat: 'https://example.org/post/$slug/$id' }"}</code></pre>
				</div>);
		case 'profileLinkFormatOptions':
			return (
				<div>
					<p>The following variables are replaced in the URL:</p>
					<ul>
						<li><strong>$id</strong> &ndash; ID of the user</li>
					</ul>
					<pre className="language-javascript"><code className="language-javascript">{"{ profileLinkFormat: 'https://example.org/profile?user=$id' }"}</code></pre>
				</div>);
		case 'authorFormatOptions':
			return (
				<div>
					<p>The following variables are replaced in the string:</p>
					<ul>
						<li><strong>$author</strong> &ndash; display name of the post author</li>
					</ul>
				</div>);
		case 'conversationLinkFormatOptions':
			return (
				<div>
					<p>The following variables are replaced in the URL:</p>
					<ul>
						<li><strong>$id</strong> &ndash; ID of the post</li>
					</ul>
				</div>);
		case 'bylineFormatOptions':
			return (
				<div>
					<p>The following variables will be replaced in the byline with values from the post:</p>
					<ul>
						<li><strong>$authorName</strong> &ndash; The post author’s name</li>
						<li><strong>$date</strong> &ndash; The date the post was published in MMM D, YYYY format</li>
						<li><strong>$date{'{format}'}</strong> &ndash; The date the post was published using a custom format string. See <Link to="/widgets/customize#date-time-formatting">Date and Time Format Strings</Link> for details.</li>
					</ul>
				</div>);
		case 'submitLinkImportResponse':
			return (
				<div>
					<p>The event handler for this event is passed an extra parameter that details the response to the import. The response object properties are:</p>
					<ul>
						<li><strong>status</strong> &ndash; Indicates whether the import succeeded. <code>"success"</code> indicates the import succeeded or partially succeeded. <code>"error"</code> indicates an invalid link URL.</li>
						<li><strong>post</strong> &ndash; The post that was imported. See <Link to="/api/responses/postResponse">postResponse</Link> for details.</li>
					</ul>
				</div>);
		case 'submitLinkSubmitResponse':
			return (
				<div>
					<p>The event handler for this event is passed an extra parameter that details the response to the import. The response object properties are:</p>
					<ul>
						<li><strong>status</strong> &ndash; Indicates whether the submission succeeded. <code>"success"</code> indicates the post was submitted to the community. <code>"error"</code> indicates the post could not be submitted.</li>
						<li><strong>unknownError</strong> &ndash; Diagnostic information about why the post submission failed.</li>
						<li>
							<strong>errors</strong> &ndash; A string array of user input error indicators. The array may be empty or contain one or more of the following:
						<ul>
								<li><code>"titleRequired"</code>: A title was not provided for the post.</li>
								<li><code>"tagRequired"</code>: Tags are required by the community on new posts and no tags were selected.</li>
							</ul>
						</li>
					</ul>
				</div>);
		case 'profileAddChannelsSubmitResponse':
			return (
				<div>
					<p>The event handler for this event is passed an extra parameter that details the response to the removal. The response object properties are:</p>
					<ul>
						<li>
							<strong>status</strong> &ndash;
								<code>"success"</code> indicates the user’s social channel was connected.
								<code>"error"</code> indicates the user’s social channel could not be connected.
						</li>
						<li>
							<strong>result</strong> &ndash; If status=<code>"success"</code>, this field contains information about the add. Otherwise, this field is not present.
						</li>
						<li>
							<strong>error</strong> &ndash; If status=<code>"error"</code>, this field contains information about the error. Otherwise, this field is not present.
						</li>
					</ul>
				</div>);
		case 'profileChannelsRemoveResponse':
			return (
				<div>
					<p>
						The event handler for this event is passed an extra parameter that details the response to the removal.
					The response object properties are:</p>
					<ul>
						<li>
							<strong>status</strong> &ndash;
						<code>"success"</code> indicates the user’s social channel was disconnected.
						<code>"error"</code> indicates the user’s social channel could not be disconnected.
					</li>
						<li>
							<strong>user</strong> &ndash; If status=<code>"success"</code>, this field contains information about the removal.
						See <Link to="/api/Users#post-user-id-removechannel-userchannelid">{'POST /v1/user/{id}/removechannel/{userChannelId}'}</Link>.
						Otherwise, this field is not present.
					</li>
						<li>
							<strong>error</strong> &ndash; If status=<code>"error"</code>, this field contains information about the error. Otherwise, this field is not present.
					</li>
					</ul>
				</div>);
		case 'profileChangePasswordResponse':
			return (
				<div>
					<p>The event handler for this event is passed an extra parameter that details the response to the submit. The response object properties are:</p>
					<ul>
						<li>
							<strong>status</strong> &ndash;
						<code>"success"</code> indicates the user’s changes were submitted.
						<code>"error"</code> indicates the user’s changes were not submitted.
					</li>
						<li>
							<strong>result</strong> &ndash; If status=<code>"success"</code>, this field contains information about the result of the change.
						See <Link to="/api/Login#post-login-changepassword">POST /v1/login/changepassword</Link>
							Otherwise, this field is not present.
					</li>
						<li>
							<strong>error</strong> &ndash; If status=<code>"error"</code>, this field contains information about the error. Otherwise, this field is not present.
					</li>
					</ul>
				</div>);
		case 'profileUserResponse':
			return (
				<div>
					<p>The event handler for this event is passed an extra parameter that details the response to the submit. The response object properties are:</p>
					<ul>
						<li><strong>status</strong>: <code>"success"</code> indicates the user's changes were submitted. <code>"error"</code> indicates the user's changes were not submitted</li>
						<li><strong>user</strong>: If status = <code>"success"</code>, this field contains the updated user. See the response object of <Link to="/api/Users#post-user-id">PUT /v1/user</Link>. Otherwise, this field is not present</li>
						<li><strong>error</strong>: If status = <code>"error"</code>, this field contains information about the error. Otherwise, this field is not present</li>
					</ul>
				</div>);
		case 'profileNotificationResponse':
			return (
				<div>
					<p>The event handler for this event is passed an extra parameter that details the response to the submit. The response object properties are:</p>
					<ul>
						<li><strong>status</strong>: <code>"success"</code> indicates the user's changes were submitted. <code>"error"</code> indicates the user's changes were not submitted</li>
						<li><strong>result</strong>: If status = <code>"success"</code>, this field contains information about the result of the change. See <Link to="/api/Users#post-user-id-notifications-preferences">{'PUT /v1/user/{id}/notifications/preferences'}</Link>.Otherwise, this field is not present</li>
						<li><strong>error</strong>: If status = <code>"error"</code>, this field contains information about the error. Otherwise, this field is not present</li>
					</ul>
				</div>);
		case 'profilePhotoSize':
			return (
				<ul>
					<li><strong>Original</strong> &ndash; Full size</li>
					<li><strong>Square160</strong> &ndash; Scaled, centered, and cropped to completely fill an 160x160 box</li>
					<li><strong>Square80</strong> &ndash; Scaled, centered, and cropped to completely fill an 80x80 box</li>
					<li><strong>Square40</strong> &ndash; Scaled, centered, and cropped to completely fill an 40x40 box</li>
					<li><strong>Square20</strong> &ndash; Scaled, centered, and cropped to completely fill an 20x20 box</li>
				</ul>);
		case 'profilePhotoResponse':
			return (
				<div>
					<p>The event handler for this event is passed an extra parameter that details the response to the submit. The response object properties are:</p>
					<ul>
						<li><strong>status</strong>: <code>"success"</code> indicates the user's changes were submitted. " error" indicates the user's changes were not submitted</li>
						<li><strong>user</strong>: If status = <code>"success"</code>, this field contains the updated user. See <Link to="/api/Users#post-user-id-profileimage">{'POST /v1/user/{id}/profileimage'}</Link>. Otherwise, this field is not present</li>
						<li><strong>error</strong>: If status = "error", this field contains information about the error. Otherwise, this field is not present</li>
					</ul>
				</div>);
		case 'profileCategorySubscriptionsResponse':
			return (
				<div>
					<p>The event handler for this event is passed an extra parameter that details the response to the submit. The response object properties are:</p>
					<ul>
						<li><strong>status</strong>: <code>"success"</code> indicates the user's changes were submitted. <code>"error"</code> indicates the user's changes were not submitted</li>
						<li><strong>result</strong>: If status = <code>"success"</code>, this field contains the updated category subscriptions. Otherwise, this field is not present</li>
						<li><strong>error</strong>: If status = <code>"error"</code>, this field contains information about the error. Otherwise, this field is not present</li>
					</ul>
				</div >);
		case 'broadcastAcknowledgedResponse':
			return (
				<div>
					<p>The event handler for this event is passed an extra parameter that details the response to the acknowlegement. The response object properties are:</p>
					<ul>
						<li><strong>readConfirmationDate</strong>: An ISO8601 Date and Time string representing the date that the viewer confirmed receipt of the broadcast</li>
					</ul>
				</div >);
		case 'profilePhotoSummary':
			return (
				<div>
					<p>You can test for support of the FileReader classes:</p >
					<pre className="language-javascript"><code className="language-javascript">
						{`if (!(window.File && window.FileReader && (typeof FileReader !== "undefined")
    && window.FileList && window.Blob && window.FormData)) {
	// ProfilePhoto not supported
}`}</code></pre>
					<p>and display an appropriate message if the support does not exist.</p>
				</div>
			);
		default:
			return null;
	}
};

// Presentational Component
const WidgetHelper = ({ helperName }) => {
	return (
		helper(helperName)
	);
};

WidgetHelper.propTypes = {
	helperName: PropTypes.string.isRequired
};

export default WidgetHelper;












// WEBPACK FOOTER //
// ./src/components/widgets/WidgetHelper.js